import React, { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Box, Card, CardContent, Chip, Collapse, Divider, LinearProgress,
  Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { DiscoveryIcon } from '../global/CustomIcons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import RunFunnel, { STAGE_META, DEFAULT_ORDER } from './RunFunnel';

const safeRelative = (d) => {
  try { if (!d) return ''; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return ''; }
};

const RUN_STATUS = {
  queued: { label: 'Queued', color: 'info' },
  running: { label: 'Live', color: 'info' },
  completed: { label: 'Completed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

/**
 * The goal's live "Discovery run" rail card: a status header, an overall
 * progress bar, the current step in plain language, the stage funnel (with
 * its rich per-stage detail preserved), and collapsible run stats.
 */
const DiscoveryRunCard = ({ run, active }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);

  const { done, total, currentId } = useMemo(() => {
    const order = run?.config_snapshot?.stage_order || DEFAULT_ORDER;
    const stages = run?.stages || {};
    const t = order.length || DEFAULT_ORDER.length;
    const d = order.filter((id) => (stages[id] || {}).status === 'completed').length;
    const cur = order.find((id) => (stages[id] || {}).status === 'running');
    return { done: d, total: t, currentId: cur };
  }, [run]);

  const status = run?.status;
  const meta = RUN_STATUS[status] || { label: 'Idle', color: 'default' };
  const pct = total ? Math.round((done / total) * 100) : 0;
  const accent = meta.color === 'default' ? theme.palette.info.main
    : theme.palette[meta.color].main;

  // ── No run yet ─────────────────────────────────────────────────────────
  if (!run) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <DiscoveryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Discovery run</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            No run yet — discovery will search your signals and events for what
            predicts this target.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const currentLabel = STAGE_META[currentId]?.label || '';
  const currentBlurb = STAGE_META[currentId]?.blurb || '';

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      {/* Live accent strip while running */}
      {active && (
        <LinearProgress sx={{ height: 3 }} color={meta.color === 'default' ? 'info' : meta.color} />
      )}
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DiscoveryIcon sx={{ fontSize: 18, color: accent }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Discovery run</Typography>
          </Stack>
          <Chip
            size="small"
            color={meta.color === 'default' ? undefined : meta.color}
            variant={status === 'completed' ? 'filled' : 'outlined'}
            label={meta.label}
            sx={{ height: 22, fontWeight: 600 }}
          />
        </Stack>

        {/* Overall progress */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 78 }}>
            {done} of {total} steps
          </Typography>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant={status === 'running' && !currentId ? 'indeterminate' : 'determinate'}
              value={status === 'completed' ? 100 : pct}
              color={meta.color === 'default' ? 'info' : meta.color}
              sx={{ height: 8, borderRadius: 4, bgcolor: alpha(accent, 0.12) }}
            />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 34, textAlign: 'right' }}>
            {status === 'completed' ? '100%' : `${pct}%`}
          </Typography>
        </Stack>

        {/* Current step — the live "what's next" line */}
        {active && currentLabel && (
          <Box sx={{
            mt: 1, p: 1.25, borderRadius: 2,
            backgroundColor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
          }}>
            <Typography variant="caption" color="info.main" sx={{ fontWeight: 700, display: 'block' }}>
              Now · {currentLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentBlurb}
            </Typography>
          </Box>
        )}
        {status === 'completed' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Finished {safeRelative(run.finished_at) || 'recently'}.
          </Typography>
        )}
        {status === 'failed' && run?.error && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
            {String(run.error)}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Stage funnel — collapsed by default; the live "Now ·" line above
            already tells you what's happening. Expand for the full path. */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          onClick={() => setStagesOpen((v) => !v)}
          sx={{ cursor: 'pointer', py: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {stagesOpen ? 'Hide steps' : `Show all ${total} steps`}
          </Typography>
          {stagesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Stack>
        <Collapse in={stagesOpen} timeout="auto" unmountOnExit>
          <RunFunnel run={run} embedded />
        </Collapse>

        {/* Collapsible run stats */}
        <Divider sx={{ my: 0.5 }} />
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          onClick={() => setOpen((v) => !v)}
          sx={{ cursor: 'pointer', py: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Run stats
          </Typography>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Stack>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={0.5} sx={{ pt: 0.5 }}>
            <StatRow label="Started" value={run?.started_at ? safeRelative(run.started_at) : '—'} />
            <StatRow label="Finished" value={run?.finished_at ? safeRelative(run.finished_at) : '—'} />
            <StatRow
              label="Window"
              value={run?.data_snapshot?.window_start
                ? `${run.data_snapshot.window_start.slice(0, 10)} → ${run.data_snapshot.window_end?.slice(0, 10)}`
                : '—'}
            />
            <StatRow
              label="Grid"
              value={run?.data_snapshot?.n_bins
                ? `${run.data_snapshot.n_bins} bins × ${run.data_snapshot.n_series} series`
                : '—'}
            />
            <StatRow label="Candidates" value={run?.data_snapshot?.n_candidates ?? '—'} />
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const StatRow = ({ label, value }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
      {label}
    </Typography>
    <Tooltip title={String(value)} arrow>
      <Typography variant="caption" noWrap>{String(value)}</Typography>
    </Tooltip>
  </Stack>
);

export default DiscoveryRunCard;
